/* Copyright (c) 2020-present CloudIO Inc */

import { DataRecord } from '@cloudio-saas/datasource-types';
import { InputAdornment, TextFieldVariants } from '@mui/material';
import { TextField as MUINumberField, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { BaseProps, Form, NumberField } from 'componentregistry';
import {
  useAttributeDirty,
  useCurrentDataStore,
  useIsEditable,
  useNumberAttribute,
} from 'components/DataStoreReference/DataStoreReference';
import { HorizontalFormField } from 'components/Form/FormUtils';
import { FieldWrapper, useFieldVariant } from 'components/TextField/BaseField';
import { useSyncControllerFunction } from 'controller/ControllerRegistry';
import NumberSlider, { NumberSliderProps } from 'custom/NumberSlider';
import { useStringExpression } from 'expression/useExpression';
import { useFocus } from 'hooks/useFocus';
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useErrorMessage } from 'store/useAppStore';
import { useTheme } from 'theme/Theme';
import useTrigger from 'triggers/useTrigger';
import { isEmpty, isNotEmpty } from 'utils/isEmpty';
import { useAudit } from '../../root/Audit';
import { useTranslate } from '../../utils/lang';
import { useChildren } from 'registry/Components';
import {
  NumberFormatValues,
  NumericFormat,
  PatternFormat,
} from 'react-number-format';

export const useNumberFieldStyles = makeStyles((theme: Theme) => ({
  numberInput: {
    MozAppearance: 'textfield',
    '&::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
    },
  },
}));

interface NumberFieldProps
  extends Required<NumberField.NumberField>,
    BaseProps<'NumberField'> {}

interface Props extends NumberFieldProps, Form.HorizontalFormProps {}

interface NumberFormatFieldProps {
  value: string | number;
  id: string;
  fullWidth: boolean;
  autoFocus: boolean;
  variant: TextFieldVariants | undefined;
  InputProps: any;
  inputProps: any;
  disabled: boolean;
  required: boolean;
  placeholder: string;
  InputLabelProps: any;
}

function validate(props: Props, _value: number): string | null {
  const { allowNegative, allowDecimal, minValue, maxValue } = props;
  if (
    !isEmpty(minValue) &&
    minValue !== Number.MIN_VALUE &&
    _value < minValue
  ) {
    return `You must enter a number greater than or equals ${minValue}!`;
  }
  if (
    !isEmpty(maxValue) &&
    maxValue !== Number.MAX_VALUE &&
    _value > maxValue
  ) {
    return `You must enter a number less than or equals ${maxValue}!`;
  }
  if (!allowNegative && _value < 0) {
    return 'You must enter only positive value';
  }
  if (!allowDecimal && Math.round(_value) !== _value) {
    return 'Decimal values are not allowed';
  }
  return null;
}

function IONumberField(
  props: Props,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const {
    appUid,
    autoFocus,
    roleUid,
    className,
    domProps,
    flex,
    formHorizontal,
    insertable,
    itemId,
    labelAlignment,
    labelColor,
    labelWidth,
    onChangeFunction,
    onKeyDownFunction,
    pageId,
    platform,
    required,
    updatable,
    useSlider,
    sliderMin,
    sliderMax,
    sliderStep,
    serverValidateOnChange,
    plain,
    viewAttribute,
    bgColorExpression,
    colorExpression,
    textFontSize,
    textBold,
    textItalic,
    labelItalic,
    labelBold,
    thousandSeparator,
    decimalScale,
    fixedDecimalScale,
    allowDecimal,
    format,
    suffix,
    prefix,
    step,
    skipScrollOnChange,
    hideSpinner,
  } = props;
  const classes = useNumberFieldStyles();
  const theme = useTheme();

  const _decimalScale =
    decimalScale === Number.MAX_VALUE
      ? allowDecimal
        ? 100
        : undefined
      : decimalScale;

  let { helperText, placeholder } = props;
  const { label } = props;
  let labelResult =
    useStringExpression({
      exp: label,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'label',
        data: {},
      },
    }) || undefined;

  const { t } = useTranslate();
  helperText = t(helperText);
  placeholder = t(placeholder);
  labelResult = t(labelResult);

  const labelColorString =
    useStringExpression({
      exp: labelColor,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'labelColor',
        data: {},
      },
    }) || undefined;

  const bgColorString =
    useStringExpression({
      exp: bgColorExpression,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'bgColorExpression',
        data: {},
      },
    }) || undefined;
  const textColorString =
    useStringExpression({
      exp: colorExpression,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'colorExpression',
        data: {},
      },
    }) || undefined;

  const variant = useFieldVariant(appUid);
  const [value, setValue] = useNumberAttribute(viewAttribute);
  const dirty = useAttributeDirty(viewAttribute);
  const inputRef = useFocus(itemId);
  const focusedValue = useRef(value);
  const currentValue = useRef(value);
  useEffect(() => {
    currentValue.current = value;
  }, [value]);
  const isFocused = useRef(false);
  const [onChangeTrigger] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnChange',
  );
  const [onFocus] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnFocus',
  );
  const [onBlurTrigger] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnBlur',
  );
  const [onValueChangeTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnValueChange',
  );
  const [onEnterKeyPress, , triggerOnTab] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnEnterKeyPress',
  );
  const [afterServerValidateTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerAfterServerValidate',
  );
  const disabled = !useIsEditable({
    appUid,
    pageId,
    itemId,
    insertable,
    updatable,
  });
  const onChangeCallback = useSyncControllerFunction<string>({
    appUid,
    pageId,
    itemId,
    functionName: onChangeFunction,
  });
  const onKeyDownCallback = useSyncControllerFunction({
    appUid,
    pageId,
    itemId,
    functionName: onKeyDownFunction,
  });
  const onFocusCallback = useSyncControllerFunction({
    appUid,
    pageId,
    itemId,
    functionName: onKeyDownFunction,
  });
  const [focused, setFocused] = useState(false);
  const onBlurCallback = useSyncControllerFunction({
    appUid,
    pageId,
    itemId,
    functionName: onKeyDownFunction,
  });
  const store = useCurrentDataStore<DataRecord>();
  const onBlur = useCallback(
    async (event?: React.FocusEvent<HTMLInputElement>) => {
      isFocused.current = false;
      setFocused(false);
      onBlurCallback({ event, eventName: 'onBlur' });
      await onBlurTrigger?.();
      if (focusedValue.current !== currentValue.current) {
        await onValueChangeTrigger?.();
        if (serverValidateOnChange && store) {
          await store.serverValidate({
            attribute: viewAttribute,
            oldValue: focusedValue.current,
          });
          await afterServerValidateTrigger?.();
        }
      }
    },
    [
      onBlurCallback,
      onBlurTrigger,
      onValueChangeTrigger,
      serverValidateOnChange,
      store,
      viewAttribute,
      afterServerValidateTrigger,
    ],
  );

  const startIcons = useChildren({
    appUid,
    pageId,
    itemId,
    itemType: 'NumberField',
  }).filter((c) => c.props.itemType === 'StartIcon');

  const endIcons = useChildren({
    appUid,
    pageId,
    itemId,
    itemType: 'NumberField',
  }).filter((c) => c.props.itemType === 'EndIcon');

  const children = useChildren({
    appUid,
    pageId,
    itemId,
    itemType: 'NumberField',
  }).filter(
    (c) => c.props.itemType !== 'StartIcon' && c.props.itemType !== 'EndIcon',
  );

  const onValueChange = (val: NumberFormatValues) => {
    const oldValue = value;
    let newValue = val.floatValue;
    if (
      (isEmpty(newValue) || !Number.isNaN(newValue)) &&
      oldValue !== newValue
    ) {
      const result = onChangeCallback({
        oldValue: value,
        value: newValue,
        eventName: 'onChange',
      });
      if (result.status === 'ERROR') return;
      if (result.status === 'SUCCESS' && typeof result.result === 'number') {
        newValue = result.result;
      }
      const errMsg =
        newValue === undefined ? undefined : validate(props, newValue);
      const recordId = store?.getCurrentRecordId();
      if (errMsg && recordId) {
        store?.setError(viewAttribute, recordId, errMsg);
      } else if (recordId && store?.hasError(viewAttribute, recordId)) {
        store?.clearError(viewAttribute, recordId);
      }
      currentValue.current = newValue;
      if (newValue || val.value === '' || /^-?0+(\.0*)?$/.test(val.value)) {
        setValue(newValue);
      }
      onChangeTrigger?.(undefined, { oldValue: value, value: newValue }).then(
        (r) => {
          if (!r) {
            currentValue.current = oldValue;
            setValue(oldValue);
          }
        },
      );
      if (platform.isRecording()) {
        platform.recordLine(
          itemId,
          `await ui.type('${itemId}', ${
            newValue === undefined ? "''" : newValue
          });`,
        );
      }
    }
  };

  const id = useId();

  const numberFormatFieldProps: NumberFormatFieldProps = {
    id,
    fullWidth: true,
    autoFocus,
    variant,
    value: value ?? '',
    InputProps: {
      endAdornment: endIcons.length ? (
        <InputAdornment position="end">{endIcons}</InputAdornment>
      ) : undefined,
      startAdornment: startIcons.length ? (
        <InputAdornment position="start">{startIcons}</InputAdornment>
      ) : undefined,
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
        isFocused.current = true;
        setFocused(true);
        onFocusCallback({ event, eventName: 'onFocus' });
        onFocus?.(event);
      },
      onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
        onBlur(event);
      },
      onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || (triggerOnTab && event.key === 'Tab')) {
          onEnterKeyPress?.();
        }
        onKeyDownCallback({ event, eventName: 'onKeyDown' });
      },
      inputRef,
      sx: plain
        ? {
            '&.Mui-disabled': {
              '& > input': {
                color: 'text.primary',
                WebkitTextFillColor: theme.palette.text.primary,
              },
              '&:before': {
                border: 0,
              },
            },
          }
        : undefined,
    },
    inputProps: {
      sx: {
        color: textColorString,
        backgroundColor: bgColorString,
        fontSize: textFontSize,
        fontWeight: textBold ? 800 : undefined,
        fontStyle: textItalic ? 'italic' : undefined,
        '&.Mui-disabled': {
          WebkitTextFillColor: `${textColorString} !important`,
          backgroundColor: bgColorString,
        },
      },
      'data-testid': `${itemId}-numberField`,
      className: hideSpinner ? classes.numberInput : '',
    },
    disabled,
    required,
    placeholder,
    InputLabelProps: {
      sx: {
        color: labelColorString,
        fontWeight: labelBold ? 800 : undefined,
        fontStyle: labelItalic ? 'italic' : undefined,
        '&.Mui-disabled': {
          color: labelColorString,
        },
      },
    },
  };

  const sliderFieldProps: NumberSliderProps = {
    value: value ?? 0,
    onChange: (e: Event, val: number | number[], activeThumb: number) => {
      const oldValue = value;
      if (Array.isArray(val)) {
        val = val[0];
      }
      let newValue = isEmpty(val) ? undefined : Number(val);
      if (isEmpty(newValue) || !Number.isNaN(newValue)) {
        const result = onChangeCallback({
          oldValue: value,
          value: newValue,
          event: e,
          eventName: 'onChange',
        });
        if (result.status === 'ERROR') return;
        if (result.status === 'SUCCESS' && typeof result.result === 'number') {
          newValue = result.result;
        }
        const errMsg =
          newValue === undefined ? undefined : validate(props, newValue);
        const recordId = store?.getCurrentRecordId();
        if (errMsg && recordId) {
          store?.setError(viewAttribute, recordId, errMsg);
        } else if (recordId && store?.hasError(viewAttribute, recordId)) {
          store?.clearError(viewAttribute, recordId);
        }
        currentValue.current = newValue;
        setValue(newValue);
        onChangeTrigger?.(e as unknown as SyntheticEvent<HTMLElement, Event>, {
          oldValue: value,
          value: newValue,
        }).then((r) => {
          if (!r) {
            currentValue.current = oldValue;
            setValue(oldValue);
          }
        });
      }
    },
    disabled,
    min: sliderMin,
    max: sliderMax,
    step: sliderStep,
    valueLabelDisplay: 'auto',
  };

  useEffect(() => {
    return () => {
      if (isFocused.current) onBlur();
    };
  }, [onBlur]);

  const errorMessage = useErrorMessage(store, viewAttribute);

  const tip = errorMessage
    ? {
        'data-tip': errorMessage,
        'data-tip-at': 'top',
        'data-tip-error': true,
        'data-tip-delay': 0,
      }
    : undefined;

  useAudit({ appUid, pageId, active: focused, viewAttribute });

  const isNumberType =
    !(prefix || suffix || thousandSeparator || fixedDecimalScale) && step;

  return (
    <FieldWrapper
      className={className}
      dirty={dirty && !disabled}
      domProps={domProps}
      flex={flex}
      ref={ref}
    >
      {useSlider ? (
        <NumberSlider {...sliderFieldProps} />
      ) : formHorizontal ? (
        <HorizontalFormField
          control={
            isNotEmpty(format) ? (
              <PatternFormat
                customInput={MUINumberField}
                {...tip}
                format={format}
                error={!!errorMessage}
                onValueChange={onValueChange}
                {...numberFormatFieldProps}
              ></PatternFormat>
            ) : (
              <NumericFormat
                customInput={MUINumberField}
                {...tip}
                thousandSeparator={thousandSeparator ? ',' : undefined}
                error={!!errorMessage}
                decimalScale={
                  !isNumberType
                    ? _decimalScale && _decimalScale > 0
                      ? _decimalScale
                      : 0
                    : undefined
                }
                onValueChange={onValueChange}
                fixedDecimalScale={allowDecimal ? fixedDecimalScale : undefined}
                suffix={suffix}
                prefix={prefix}
                {...numberFormatFieldProps}
                inputProps={{
                  ...numberFormatFieldProps.inputProps,
                  type: isNumberType ? 'number' : 'text',
                  step: step ? Number(step) : 1,
                }}
                InputProps={{
                  ...numberFormatFieldProps.InputProps,
                  onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
                    if (e && skipScrollOnChange && !hideSpinner) {
                      const inputElement =
                        numberFormatFieldProps.InputProps?.inputRef?.current;
                      if (inputElement) {
                        inputElement.blur();
                      }
                    }
                  },
                }}
              />
            )
          }
          label={labelResult}
          labelAlignment={labelAlignment}
          labelWidth={labelWidth}
          error={!!errorMessage}
          required={required}
          helperText={errorMessage || helperText}
          labelColor={labelColorString}
          fontWeight={labelBold ? 800 : undefined}
          fontStyle={labelItalic}
          id={id}
        />
      ) : isNotEmpty(format) ? (
        <PatternFormat
          customInput={MUINumberField}
          label={labelResult}
          {...tip}
          format={format}
          helperText={errorMessage || helperText}
          error={!!errorMessage}
          onValueChange={onValueChange}
          {...numberFormatFieldProps}
        ></PatternFormat>
      ) : (
        <NumericFormat
          customInput={MUINumberField}
          {...tip}
          label={labelResult}
          thousandSeparator={thousandSeparator ? ',' : undefined}
          helperText={errorMessage || helperText}
          error={!!errorMessage}
          decimalScale={
            !isNumberType
              ? _decimalScale && _decimalScale > 0
                ? _decimalScale
                : 0
              : undefined
          }
          onValueChange={onValueChange}
          fixedDecimalScale={allowDecimal ? fixedDecimalScale : undefined}
          suffix={suffix}
          prefix={prefix}
          {...numberFormatFieldProps}
          inputProps={{
            ...numberFormatFieldProps.inputProps,
            type: isNumberType ? 'number' : 'text',
            step: step ? Number(step) : 1,
          }}
          InputProps={{
            ...numberFormatFieldProps.InputProps,
            onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
              if (e && skipScrollOnChange && !hideSpinner) {
                const inputElement =
                  numberFormatFieldProps.InputProps?.inputRef?.current;
                if (inputElement) {
                  inputElement.blur();
                }
              }
            },
          }}
        />
      )}
      {children}
    </FieldWrapper>
  );
}

export default React.forwardRef(IONumberField);
